// src/components/common/SkeletonCard.js

import React from 'react';
import { Box, Skeleton, Card, CardContent } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { skeletonStyles, liquidGlassCardDark } from '../../theme/designTokens';

/**
 * スケルトンカードコンポーネント
 * ローディング中に表示するカード型スケルトン
 *
 * @param {string} variant - カードの種類 ('default' | 'kpi' | 'chart' | 'list')
 * @param {number} height - カードの高さ（オプション）
 * @param {boolean} useGlass - Liquid Glass効果を使用するか
 */
export function SkeletonCard({ variant = 'default', height, useGlass = false }) {
  const theme = useTheme();
  const cardSx = useGlass ? liquidGlassCardDark(theme) : { borderRadius: '12px' };

  switch (variant) {
    case 'kpi':
      return (
        <Card sx={{ ...cardSx, height: height || 140 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
              <Skeleton variant="rounded" width={48} height={48} animation="wave" />
              <Skeleton variant="rounded" width={60} height={24} animation="wave" />
            </Box>
            <Skeleton variant="text" width="40%" height={20} animation="wave" sx={{ mb: 1 }} />
            <Skeleton variant="text" width="60%" height={40} animation="wave" />
          </CardContent>
        </Card>
      );

    case 'chart':
      return (
        <Card sx={{ ...cardSx, height: height || 300 }}>
          <CardContent>
            <Skeleton variant="text" width="30%" height={28} animation="wave" sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={200} animation="wave" sx={{ borderRadius: '8px' }} />
          </CardContent>
        </Card>
      );

    case 'list':
      return (
        <Card sx={{ ...cardSx, height: height || 'auto' }}>
          <CardContent>
            <Skeleton variant="text" width="40%" height={28} animation="wave" sx={{ mb: 2 }} />
            {[1, 2, 3].map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Skeleton variant="circular" width={40} height={40} animation="wave" sx={{ mr: 2 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="70%" height={20} animation="wave" />
                  <Skeleton variant="text" width="50%" height={16} animation="wave" />
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      );

    case 'form':
      return (
        <Card sx={{ ...cardSx, height: height || 'auto' }}>
          <CardContent>
            <Skeleton variant="text" width="50%" height={32} animation="wave" sx={{ mb: 3 }} />
            {[1, 2, 3].map((item) => (
              <Box key={item} sx={{ mb: 3 }}>
                <Skeleton variant="text" width="30%" height={20} animation="wave" sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={56} animation="wave" sx={{ borderRadius: '8px' }} />
              </Box>
            ))}
            <Skeleton variant="rectangular" width="100%" height={48} animation="wave" sx={{ borderRadius: '8px', mt: 2 }} />
          </CardContent>
        </Card>
      );

    default:
      return (
        <Card sx={{ ...cardSx, height: height || 200 }}>
          <CardContent>
            <Skeleton variant="text" width="60%" height={28} animation="wave" sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={80} animation="wave" sx={{ borderRadius: '8px', mb: 2 }} />
            <Skeleton variant="text" width="80%" animation="wave" />
            <Skeleton variant="text" width="40%" animation="wave" />
          </CardContent>
        </Card>
      );
  }
}

/**
 * KPIグリッドスケルトン
 * ダッシュボードのKPIカード群のスケルトン
 */
export function SkeletonKPIGrid({ count = 4 }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
        },
        gap: 2,
      }}
    >
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonCard key={index} variant="kpi" useGlass />
      ))}
    </Box>
  );
}

/**
 * ダッシュボード全体のスケルトン
 */
export function SkeletonDashboard() {
  return (
    <Box sx={{ p: 3 }}>
      {/* KPIセクション */}
      <SkeletonKPIGrid count={4} />

      {/* メインコンテンツ */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 3,
          mt: 3,
        }}
      >
        <SkeletonCard variant="chart" useGlass />
        <SkeletonCard variant="list" useGlass />
      </Box>

      {/* 下部セクション */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3,
          mt: 3,
        }}
      >
        <SkeletonCard variant="default" useGlass />
        <SkeletonCard variant="default" useGlass />
      </Box>
    </Box>
  );
}

/**
 * ログインフォームスケルトン
 */
export function SkeletonLoginForm() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 4,
        maxWidth: 400,
        mx: 'auto',
      }}
    >
      <Skeleton variant="circular" width={56} height={56} animation="wave" sx={{ mb: 2 }} />
      <Skeleton variant="text" width="60%" height={32} animation="wave" sx={{ mb: 3 }} />

      <Box sx={{ width: '100%' }}>
        <Skeleton variant="rectangular" height={56} animation="wave" sx={{ borderRadius: '8px', mb: 2 }} />
        <Skeleton variant="rectangular" height={56} animation="wave" sx={{ borderRadius: '8px', mb: 3 }} />
        <Skeleton variant="rectangular" height={48} animation="wave" sx={{ borderRadius: '8px' }} />
      </Box>
    </Box>
  );
}

export default SkeletonCard;
